import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Stack, Typography, FormHelperText } from '@mui/material';
import { FormProvider, useForm, SubmitHandler, Control } from 'react-hook-form';
import { collectionsProvider } from '@/providers';
import { NotificationTypeEnum, CollectionsModel } from '@/types';
import { appSetNotification } from '@/store';
import { MyButton, MyDialog, FormInput, MyAutocomplete } from '@/components';
import styles from './AdsListItem.module.scss';

interface CreateSelectionsModel {
  newFolder: string;
  folders: string[];
}

interface IErrors {
  [key: string]: { message: string } | undefined;
}

export const DialogSelection = ({
  userId,
  open,
  onClose,
  titleText,
  listingId,
}: {
  userId: string;
  titleText: string;
  listingId: string;
  open: boolean;
  onClose: () => void;
}) => {
  const dispatch = useDispatch();
  const [collections, setCollections] = useState<CollectionsModel[]>([]);
  const [collectionsName, setCollectionsName] = useState<string[]>([]);
  const [openDialogSuccess, setOpenDialogSuccess] = useState<boolean>(false);

  const formMethods = useForm<CreateSelectionsModel>({});
  const {
    handleSubmit,
    formState: { errors },
    control,
    watch,
    reset,
    setError,
    clearErrors,
  } = formMethods;

  const newFolder = watch('newFolder');
  const folders = watch('folders') ?? [];

  useEffect(() => {
    const getCollections = async () => {
      const { ok, data, status, message } = await collectionsProvider.getCollections();
      if (ok && Array.isArray(data)) {
        setCollectionsName(data.map((collection) => collection.collectionName));
        setCollections(data);
      } else {
        dispatch(appSetNotification(NotificationTypeEnum.Error, message, status));
      }
    };

    getCollections();
  }, []);

  const handleClose = () => {
    reset();
    onClose();
  };

  const submitHandler: SubmitHandler<CreateSelectionsModel> = async (values) => {
    const trimmedNewFolder = values.newFolder?.trim();
    const isDuplicate = trimmedNewFolder && collectionsName.includes(trimmedNewFolder);
    if (isDuplicate) {
      setError('newFolder', {
        type: 'manual',
        message: 'You already have a collection with this name',
      });
      return;
    }
    clearErrors('newFolder');

    let newlyCreatedCollection: CollectionsModel | null = null;
    if (trimmedNewFolder) {
      const createPayload = {
        collectionName: trimmedNewFolder,
        userId,
      };
      const { ok, data, status, message } =
        await collectionsProvider.createNewCollection(createPayload);
      if (!ok || !data) {
        dispatch(appSetNotification(NotificationTypeEnum.Error, message, status));
        return;
      }
      newlyCreatedCollection = data as CollectionsModel;
    }

    const combinedFolders = [...(values.folders ?? [])];
    if (trimmedNewFolder) {
      combinedFolders.push(trimmedNewFolder);
    }
    const uniqueFolderNames = Array.from(
      new Set(combinedFolders.map((name) => name.trim()).filter((name) => name.length > 0))
    );

    const folderIds: string[] = uniqueFolderNames
      .map((name) => {
        if (name === trimmedNewFolder && newlyCreatedCollection) {
          return newlyCreatedCollection.id;
        }
        const existing = collections.find((c) => c.collectionName === name);
        return existing?.id ?? '';
      })
      .filter((id): id is string => id !== '');

    if (folderIds && listingId) {
      const saveCollections = {
        collections: folderIds,
        adId: listingId,
      };
      const { ok, status, message } = await collectionsProvider.addListing(saveCollections);
      if (!ok) {
        dispatch(appSetNotification(NotificationTypeEnum.Error, message, status));
        return;
      } else {
        setOpenDialogSuccess(true);
      }
    }
  };

  const closeDialogSuccess = () => {
    setOpenDialogSuccess(false);
    handleClose();
  };

  return (
    <>
      <MyDialog
        open={open}
        onClose={handleClose}
        dialogTitle='Select where you want to save the collection'
        width='715'
      >
        <FormProvider {...formMethods}>
          <Box component='form'>
            <Box>
              <FormInput
                uiVariant='blank'
                name='newFolder'
                placeholder='Enter folder name'
                label='Create new folder'
              />
            </Box>
            {collectionsName.length > 0 && (
              <Box>
                <FormHelperText
                  sx={{ color: '#2a3842', fontSize: '15px', fontWeight: 400, marginTop: '20px' }}
                >
                  Select folder
                </FormHelperText>
                {/* <FormMultiSelect data={respUser} name='folders' /> */}
                <MyAutocomplete
                  errors={errors as IErrors}
                  control={control as unknown as Control}
                  data={{
                    name: 'folders',
                    options: collectionsName,
                    placeholder: 'Select folder',
                    multiple: true,
                  }}
                />
              </Box>
            )}

            <Box className={styles.submitButtons}>
              <MyButton
                data={{
                  buttonName: 'Save',
                  customWidth: '226px',
                  variant: 'contained',
                  buttonType: 'button',
                }}
                disabled={!newFolder && folders?.length === 0}
                onClick={handleSubmit(submitHandler)}
              />
              <MyButton
                data={{
                  buttonName: 'Cancel',
                  customWidth: '226px',
                  styleType: 'cancel',
                  variant: 'outlined',
                  buttonType: 'button',
                }}
                onClick={handleClose}
              />
            </Box>
          </Box>
        </FormProvider>
      </MyDialog>

      <MyDialog
        open={openDialogSuccess}
        onClose={closeDialogSuccess}
        dialogTitle='Congratulations!'
        width='650'
      >
        <Stack spacing={2} sx={{ width: '100%' }}>
          <Typography
            style={{
              textAlign: 'center',
              color: '#2A3842',
              fontSize: '18px',
              fontWeight: 400,
            }}
          >
            {`You have added `}
            <span style={{ fontWeight: 600, color: '#1650FF' }}>{titleText}</span>
            {` to the collections.`}
          </Typography>

          <MyButton
            data={{
              buttonName: 'Okay',
              variant: 'contained',
              customWidth: '358px',
              styleType: 'dialog',
            }}
            onClick={closeDialogSuccess}
          />
        </Stack>
      </MyDialog>
    </>
  );
};
